import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  TrendingUp, 
  DollarSign, 
  Target, 
  Rocket, 
  Shield, 
  Globe2,
  BarChart3,
  Lightbulb,
  Building2,
  Cpu
} from 'lucide-react';

const InvestmentSection = () => {
  const [activeTab, setActiveTab] = useState('opportunity');
  
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const marketOpportunity = [
    {
      sector: 'AI & LLM Market',
      size: '$150B+',
      growth: '35% CAGR',
      description: 'Explosive growth in AI model deployment and optimization',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      sector: 'Edge Computing',
      size: '$91B+',
      growth: '38% CAGR',
      description: 'Critical need for intelligent edge data processing',
      color: 'from-green-500 to-emerald-600'
    },
    {
      sector: 'Media & Streaming',
      size: '$185B+',
      growth: '21% CAGR', 
      description: 'Demand for efficient content delivery and storage',
      color: 'from-purple-500 to-pink-600'
    },
    {
      sector: 'Autonomous Vehicles',
      size: '$76B+',
      growth: '40% CAGR',
      description: 'Real-time processing requirements for safety-critical systems',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const businessModel = [
    {
      type: 'Licensing',
      icon: Shield,
      revenue: 'Patent & IP Licensing',
      description: 'License patented MSML technology to enterprise customers',
      examples: ['OEM partnerships', 'Technology licensing deals', 'IP portfolio monetization']
    },
    {
      type: 'SaaS',
      icon: Globe2,
      revenue: 'Software as a Service',
      description: 'Cloud-based IDDO solutions with subscription model',
      examples: ['cDM Cloud platform', 'API-based services', 'Usage-based pricing']
    },
    {
      type: 'PaaS',
      icon: Building2,
      revenue: 'Platform as a Service',
      description: 'Complete infrastructure solutions for enterprise deployment',
      examples: ['White-label platforms', 'Custom implementations', 'Managed services']
    },
    {
      type: 'Direct Sales',
      icon: Cpu,
      revenue: 'Product Implementation',
      description: 'Direct sales of specialized MSML-powered products',
      examples: ['D1 AUDIO codec', 'AudioMine platform', 'Enterprise solutions']
    }
  ];

  const competitiveAdvantages = [
    {
      title: 'Worldwide Patent Protection',
      description: 'Multiple patent grants. World-Wide coverage. Series of Fortified Patent applications.',
      icon: Shield
    },
    {
      title: 'Proven Technology',
      description: 'Demonstrable products across multiple industry sectors',
      icon: Target
    },
    {
      title: 'Cross-Industry Applications',
      description: 'Scalable solution addressing multiple $100B+ markets',
      icon: Globe2
    },
    {
      title: 'First-Mover Advantage',
      description: 'Pioneer in intelligent data density optimization',
      icon: Rocket
    }
  ];

  return (
    <section id="investment" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-blue-900/20" />
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-px h-20 bg-gradient-to-b from-cyan-400/30 to-transparent"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 0.5, 0],
              y: [0, -50]
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Investment <span className="text-cyan-400">Opportunity</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Advanced MSML technology positioned at the intersection of multiple high-growth markets with proven scientific viability
          </p>
        </motion.div>

        {/* Detailed Investment Information */}
        <Tabs defaultValue="opportunity" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1 h-auto bg-slate-800 border border-cyan-400/20 mb-8 p-1">
            <TabsTrigger value="opportunity" className="whitespace-normal h-auto py-2 text-xs sm:text-sm text-center leading-tight data-[state=active]:bg-cyan-400/20 data-[state=active]:text-cyan-400">
              Market Opportunity
            </TabsTrigger>
            <TabsTrigger value="model" className="whitespace-normal h-auto py-2 text-xs sm:text-sm text-center leading-tight data-[state=active]:bg-cyan-400/20 data-[state=active]:text-cyan-400">
              Business Model
            </TabsTrigger>
            <TabsTrigger value="advantages" className="whitespace-normal h-auto py-2 text-xs sm:text-sm text-center leading-tight data-[state=active]:bg-cyan-400/20 data-[state=active]:text-cyan-400">
              Competitive Edge
            </TabsTrigger>
            <TabsTrigger value="contact" className="whitespace-normal h-auto py-2 text-xs sm:text-sm text-center leading-tight data-[state=active]:bg-cyan-400/20 data-[state=active]:text-cyan-400">
              Investor Relations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="opportunity" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Multi-Billion Dollar Market Opportunity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {marketOpportunity.map((market, index) => (
                  <motion.div
                    key={market.sector}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-400/40 transition-all h-full">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center justify-between">
                          <span>{market.sector}</span>
                          <Badge className={`bg-gradient-to-r ${market.color} text-white`}>
                            {market.growth}
                          </Badge>
                        </CardTitle>
                        <div className="text-3xl font-bold text-cyan-400">{market.size}</div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300">{market.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="model" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Diversified Revenue Streams</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {businessModel.map((model, index) => {
                  const IconComponent = model.icon;
                  return (
                    <motion.div
                      key={model.type}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                    >
                      <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-400/40 transition-all h-full">
                        <CardHeader>
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-cyan-400/20 flex items-center justify-center">
                              <IconComponent className="text-cyan-400" size={20} />
                            </div>
                            <div>
                              <CardTitle className="text-white">{model.type}</CardTitle>
                              <CardDescription className="text-cyan-400">{model.revenue}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-300 mb-4">{model.description}</p>
                          <div className="space-y-2">
                            {model.examples.map((example, i) => (
                              <div key={i} className="flex items-center text-sm text-gray-400">
                                <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></div>
                                {example}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="advantages" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Sustainable Competitive Advantages</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {competitiveAdvantages.map((advantage, index) => {
                  const IconComponent = advantage.icon;
                  return (
                    <motion.div
                      key={advantage.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-400/40 transition-all h-full">
                        <CardHeader>
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                              <IconComponent className="text-cyan-400" size={20} />
                            </div>
                            <div>
                              <CardTitle className="text-white text-lg">{advantage.title}</CardTitle>
                              <CardDescription className="text-gray-300 mt-2">
                                {advantage.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="contact" className="mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="max-w-4xl mx-auto">
                <Card className="bg-slate-800/50 border-cyan-400/20">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl text-white">Investor Relations</CardTitle>
                    <CardDescription className="text-gray-300">
                      Connect with our team to learn more about investment opportunities
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-cyan-400">Investment Highlights</h4>
                        <ul className="space-y-3 text-gray-300">
                          <li className="flex items-center">
                            <TrendingUp className="text-green-400 mr-2" size={16} />
                            Patented technology with significant lifetime value potential
                          </li>
                          <li className="flex items-center">
                            <DollarSign className="text-green-400 mr-2" size={16} />
                            Multiple revenue streams across high-growth markets
                          </li>
                          <li className="flex items-center">
                            <Target className="text-green-400 mr-2" size={16} />
                            Proven products with commercial validation
                          </li>
                          <li className="flex items-center">
                            <Lightbulb className="text-green-400 mr-2" size={16} />
                            First-mover advantage in IDDO technology
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-6">
                        <h4 className="text-lg font-semibold text-cyan-400">Next Steps</h4>
                        <div className="space-y-4">
                          <Button 
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                            onClick={() => window.location.href = 'mailto:investors@generalharmonics.com'}
                          >
                            Request Investor Deck
                          </Button>
                          <Button 
                            variant="outline"
                            className="w-full border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-slate-900"
                            onClick={() => scrollToSection('#contact')}
                          >
                            Schedule Presentation
                          </Button>
                          <div className="text-center text-sm text-gray-400">
                            Email: investors@generalharmonics.com<br/>
                            Phone: +1 (519) 870 5831
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default InvestmentSection;